import { computed, ref, watch } from "vue";
import { checkDonorReceiptInformation } from "../../infrastructure/api/donationApi";
import { logger } from "@/infrastructure/services/LoggerService";
/**
 * Asks OPDON what the selected donor is missing for a tax receipt (OP-7954). Not debounced: the
 * pickers emit an id on selection, not on every keystroke.
 *
 * A 503 surfaces as `unavailable`, never as a list of fields: "could not read" is not "not filled
 * in", and a fabricated list is worse than letting the save fail with the real reason.
 */
export function useDonorReceiptInformation(options) {
    const gaps = ref([]);
    // Separate from `gaps` on purpose: workspace settings depend on no donor, so this must survive
    // a picker change, a cleared picker and a failed donor check. Keeping it in `gaps` did none.
    const workspaceGaps = ref([]);
    const isChecking = ref(false);
    const unavailable = ref(false);
    // An empty gap list means "complete" only once an answer came back for the CURRENT donor:
    // without this the CTA enables mid-flight, on a verdict nobody has given.
    const answered = ref(false);
    // Only the newest request may write the result: the user can change donor faster than OPDON answers.
    let latestRequestId = 0;
    // The last donor query, kept so `retry()` can replay it. A failed check otherwise leaves Save
    // disabled with no way forward: `enabled` and the ids have not changed, so no watcher re-fires,
    // and the user has to clear and re-pick the donor to discover that the outage was transient.
    let lastQuery = null;
    const hasDonor = computed(() => Boolean(options.contactId.value || options.organizationId.value));
    // A donor gap is fixed from the record just picked; an issuing-organisation gap is not, and
    // belongs at the top of the form rather than under "Contact".
    const donorGaps = computed(() => gaps.value.filter((gap) => gap.scope !== "beneficiary"));
    /**
     * What the top notice shows: the open-time probe UNION whatever beneficiary gap the donor answer
     * reports. A union, never an overwrite — overwriting is what made it flicker. Without the union a
     * beneficiary gap seen only by the donor answer still disabled Save while no notice explained it,
     * which is the one thing a blocked form must never do.
     */
    const issuerGaps = computed(() => {
        const seen = new Set(workspaceGaps.value.map((gap) => gap.scope));
        return [
            ...workspaceGaps.value,
            ...gaps.value.filter((gap) => gap.scope === "beneficiary" && !seen.has(gap.scope)),
        ];
    });
    /**
     * True once we know the receipt can be produced. Unknown (not yet checked, or 503) is NOT
     * complete — and neither is a complete donor on a workspace that is missing its own information.
     */
    const isComplete = computed(() => hasDonor.value &&
        answered.value &&
        !unavailable.value &&
        gaps.value.length === 0 &&
        workspaceGaps.value.length === 0);
    /** Clears the DONOR verdict only — the workspace one outlives every picker change. */
    function reset() {
        gaps.value = [];
        unavailable.value = false;
        isChecking.value = false;
        answered.value = false;
    }
    async function run(query, requestId) {
        try {
            const result = await checkDonorReceiptInformation(query);
            if (requestId !== latestRequestId)
                return;
            gaps.value = result.gaps;
            // `workspaceGaps` is NOT fed here although the response carries it: deriving it from the
            // donor answer makes it flicker on every picker change and vanish on a 503.
            unavailable.value = false;
            answered.value = true;
        }
        catch (error) {
            if (requestId !== latestRequestId)
                return;
            // No toast: the notice renders the "could not check" state. R47 — a silent catch still logs.
            logger.error("Donor tax-receipt completeness check failed", error);
            gaps.value = [];
            unavailable.value = true;
            answered.value = false;
        }
        finally {
            if (requestId === latestRequestId)
                isChecking.value = false;
        }
    }
    // Runs on open: the workspace question does not involve a donor.
    watch(() => options.enabled.value, async (enabled) => {
        if (!enabled)
            return;
        try {
            const result = await checkDonorReceiptInformation({});
            workspaceGaps.value = result.gaps.filter((gap) => gap.scope === "beneficiary");
        }
        catch (error) {
            // No answer, no claim: no notice rather than an invented one. R47 — silent catch logs.
            logger.error("Workspace tax-receipt completeness check failed", error);
            workspaceGaps.value = [];
        }
    }, { immediate: true });
    watch(() => [options.enabled.value, options.contactId.value, options.organizationId.value], ([enabled, contactId, organizationId]) => {
        if (!enabled || (!contactId && !organizationId)) {
            latestRequestId += 1;
            lastQuery = null;
            reset();
            return;
        }
        // An organisation donation checks the organisation AND its referent contact.
        const query = organizationId
            ? { organizationId, referentContactId: contactId || undefined }
            : { contactId };
        lastQuery = query;
        const requestId = ++latestRequestId;
        isChecking.value = true;
        // The previous verdict belonged to the previous donor: clear it, don't just stop trusting it.
        // Keeping the fields on screen while a new donor is checked names gaps on a record the user
        // is no longer looking at — the notice hides until an answer for THIS donor comes back.
        gaps.value = [];
        unavailable.value = false;
        answered.value = false;
        void run(query, requestId);
    }, { immediate: true });
    /**
     * Replays the last donor check. Exposed for the notice's retry action: every rejection lands in
     * the same "unavailable" state — 403, 404, 500, a dropped connection — and only a 503 is really
     * about OPDON being unable to answer. The others are worth one more try before the user is told
     * to start over.
     */
    async function retry() {
        if (!lastQuery || isChecking.value)
            return;
        const requestId = ++latestRequestId;
        isChecking.value = true;
        gaps.value = [];
        unavailable.value = false;
        answered.value = false;
        await run(lastQuery, requestId);
    }
    return {
        gaps,
        workspaceGaps,
        issuerGaps,
        donorGaps,
        isChecking,
        unavailable,
        isComplete,
        hasDonor,
        retry,
    };
}
